import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { AlignJustify, List } from 'lucide-react';
import { useTableDensity, TableDensity } from '../shared/hooks/useTableDensity';
import { GRAY_300, GRAY_500 } from 'theme/grayScale';

const ICON_SIZE = 16;

const styles = {
  button: {
    padding: '0.375rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    backgroundColor: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: GRAY_500,
    transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
    flexShrink: 0,
  } as React.CSSProperties,
  buttonActive: {
    color: '#6b46c1',
    border: '1px solid #6b46c1',
  } as React.CSSProperties,
};

function tooltipTitle(density: TableDensity): string {
  return density === 'standard'
    ? 'Switch to compact rows'
    : 'Switch to standard rows';
}

/**
 * Icon button that toggles table row density between 'standard' (52 px) and
 * 'compact' (36 px). Reads and writes the shared `useTableDensity` state so
 * all pages reflect the same preference.
 *
 * AlignJustify icon = standard density (spacious rows)
 * List icon         = compact density (tight rows)
 */
export function DensityToggle(): React.ReactElement {
  const { density, setDensity } = useTableDensity();

  function handleClick(): void {
    setDensity(density === 'standard' ? 'compact' : 'standard');
  }

  const isCompact = density === 'compact';

  return (
    <Tooltip title={tooltipTitle(density)} placement="bottom">
      <button
        type="button"
        onClick={handleClick}
        aria-label={tooltipTitle(density)}
        aria-pressed={isCompact}
        style={{
          ...styles.button,
          ...(isCompact ? styles.buttonActive : {}),
        }}>
        {isCompact ? (
          <List size={ICON_SIZE} />
        ) : (
          <AlignJustify size={ICON_SIZE} />
        )}
      </button>
    </Tooltip>
  );
}
